import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Monitor, Tablet, Smartphone, Lock, ChevronDown, Play, Loader2,
  Building2, ShoppingBag, ArrowRight, Clock, Sparkles, Infinity as InfinityIcon,
  CreditCard, AlertTriangle, Terminal, KeyRound, Cookie,
  Globe, Store, Upload, FileUp, Image as ImageIcon, X, Apple, Bot,
} from "lucide-react";
import { SimLayout } from "@/sim/SimLayout";
import SimBilling from "@/sim/SimBilling";
import { ENV_MODES, VIEWPORTS, createSim, createAppSim, getRecent, getBilling, confirmCardSetup, openBillingPortal } from "@/sim/lib";
import { cn } from "@/lib/utils";

const VP_ICONS = { desktop_1440: Monitor, tablet_768: Tablet, mobile_390: Smartphone };

const MODES = [
  { k: "web", label: "Web URL", icon: Globe },
  { k: "store", label: "App Store URL", icon: Store },
  { k: "app", label: "App File Upload", icon: Upload },
];

const MAX_APP_MB = 80;
const MAX_IMG_MB = 12;

export default function SimHome() {
  const navigate = useNavigate();
  const [mode, setMode] = useState("web");
  const [url, setUrl] = useState("");
  const [appFile, setAppFile] = useState(null);
  const [appShot, setAppShot] = useState(null);
  const [env, setEnv] = useState("b2b_saas");
  const [viewport, setViewport] = useState("desktop_1440");
  const [vpTouched, setVpTouched] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [auth, setAuth] = useState({ type: "form", username: "", password: "", token: "", login_url: "" });
  const [localHelp, setLocalHelp] = useState(false);
  const [loading, setLoading] = useState(false);
  const [recent, setRecent] = useState([]);
  const [billing, setBilling] = useState(null);
  const [billingLoading, setBillingLoading] = useState(true);

  const refreshBilling = () => getBilling().then(setBilling).catch(() => {}).finally(() => setBillingLoading(false));

  useEffect(() => { getRecent().then(setRecent).catch(() => {}); }, []);
  useEffect(() => { if (!vpTouched) setViewport(ENV_MODES[env].defaultViewport); }, [env, vpTouched]);

  // Load billing + handle return from Stripe card setup
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("billing") === "success" && params.get("session_id")) {
      confirmCardSetup(params.get("session_id"))
        .then(() => toast.success("Card saved — your 3 free simulations are ready"))
        .catch(() => toast.error("We couldn't confirm your card. Please try again."))
        .finally(() => { window.history.replaceState(null, "", "/sim"); refreshBilling(); });
    } else {
      if (params.get("billing")) window.history.replaceState(null, "", "/sim");
      refreshBilling();
    }
  }, []);

  const handleRunError = (e) => {
    if (e.code === "card_required") {
      toast.error("Please add a card to run simulations");
      setBilling((b) => ({ ...(b || {}), requires_card: true }));
    } else if (e.code === "payment_failed") {
      toast.error("We couldn't charge your card. Please update your payment method.");
      setBilling((b) => ({ ...(b || {}), subscription_status: "past_due" }));
    } else if (e.code === "local_url_needs_tunnel") {
      toast.error("Local URLs can't be reached from the cloud — use a tunnel (see the tip below)");
      setLocalHelp(true);
    } else {
      toast.error(e.message || "Could not start simulation");
    }
    setLoading(false);
  };

  const run = async () => {
    if (billing?.subscription_status === "past_due") {
      toast.error("Please update your payment method to continue");
      openPortal();
      return;
    }
    if (mode === "app") return runApp();

    if (!url.trim()) {
      toast.error(mode === "store" ? "Paste an App Store or Google Play listing URL" : "Enter a product or web URL to simulate");
      return;
    }
    setLoading(true);
    try {
      const hasAuth = mode === "web" && (auth.username || auth.token || (auth.type === "form" && auth.login_url));
      const body = {
        target_url: url.trim(),
        environment_mode: env,
        viewport_override: viewport,
        ...(mode === "store" ? { source_type: "app_store" } : {}),
        ...(hasAuth ? { auth_credentials: auth } : {}),
      };
      const res = await createSim(body);
      navigate(`/sim/s/${res.simulation_id}`);
    } catch (e) {
      handleRunError(e);
    }
  };

  const runApp = async () => {
    if (!appFile) { toast.error("Choose a .apk or .ipa file to analyze"); return; }
    setLoading(true);
    try {
      const res = await createAppSim({ appFile, screenshot: appShot, environmentMode: env });
      navigate(`/sim/s/${res.simulation_id}`);
    } catch (e) {
      handleRunError(e);
    }
  };

  const onPickApp = (file) => {
    if (!file) return;
    const name = file.name.toLowerCase();
    if (!name.endsWith(".apk") && !name.endsWith(".ipa")) {
      toast.error("Upload a .apk (Android) or .ipa (iOS) package");
      return;
    }
    if (file.size > MAX_APP_MB * 1024 * 1024) { toast.error(`App file too large (max ${MAX_APP_MB} MB)`); return; }
    setAppFile(file);
  };

  const onPickShot = (file) => {
    if (!file) return;
    if (!/\.(png|jpe?g|webp)$/i.test(file.name)) { toast.error("Screenshot must be a PNG or JPEG image"); return; }
    if (file.size > MAX_IMG_MB * 1024 * 1024) { toast.error(`Screenshot too large (max ${MAX_IMG_MB} MB)`); return; }
    setAppShot(file);
  };

  const [portalLoading, setPortalLoading] = useState(false);
  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const { url: portalUrl } = await openBillingPortal(window.location.origin);
      window.location.href = portalUrl;
    } catch (e) {
      toast.error(e.message || "Could not open billing portal");
      setPortalLoading(false);
    }
  };

  if (billingLoading) {
    return (
      <SimLayout maxWidth="max-w-5xl">
        <div className="py-20 flex justify-center"><Loader2 className="w-7 h-7 text-[#00F0FF] animate-spin" /></div>
      </SimLayout>
    );
  }

  if (billing?.requires_card) {
    return <SimBilling billing={billing} />;
  }

  const planActive = billing?.plan_active;
  const remaining = billing?.trial_sims_remaining ?? 0;
  const pastDue = billing?.subscription_status === "past_due";
  const complimentary = billing?.complimentary;
  const canManageBilling = billing && !complimentary && (billing.has_billing_account || planActive || pastDue);
  const runDisabled = loading || pastDue || (mode === "app" && !appFile);

  return (
    <SimLayout maxWidth="max-w-5xl">
      {/* Past-due banner */}
      {pastDue && (
        <div
          data-testid="sim-pastdue-banner"
          className="mb-4 rounded-xl border border-[#EF4444]/40 bg-[#EF4444]/10 px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3"
        >
          <AlertTriangle className="w-5 h-5 text-[#EF4444] shrink-0" />
          <div className="flex-1">
            <p className="text-[#F5F5F5] text-sm font-semibold">Your last payment failed</p>
            <p className="text-[#D4D4D8] text-xs mt-0.5">Simulations are paused until you update your card.</p>
          </div>
          <button
            data-testid="sim-pastdue-update-btn"
            onClick={openPortal}
            disabled={portalLoading}
            className="h-9 px-4 rounded-lg bg-[#EF4444] text-white font-bold text-xs flex items-center justify-center gap-2 hover:bg-[#dc2626] transition-colors disabled:opacity-60"
          >
            {portalLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CreditCard className="w-3.5 h-3.5" />}
            Update payment method
          </button>
        </div>
      )}

      <div className="sim-grid-bg -mx-5 px-5 pt-10 pb-12 rounded-none">
        {/* Billing status badge */}
        {billing && (
          <div className="flex flex-col items-center gap-1.5 mb-5" data-testid="sim-billing-badge">
            {complimentary ? (
              <span className="sim-mono inline-flex items-center gap-2 text-[11px] text-[#F59E0B] border border-[#F59E0B]/25 bg-[#F59E0B]/5 rounded-full px-3 py-1" data-testid="sim-admin-badge">
                <InfinityIcon className="w-3.5 h-3.5" /> ADMIN · UNLIMITED ACCESS
              </span>
            ) : planActive ? (
              <span className="sim-mono inline-flex items-center gap-2 text-[11px] text-[#10B981] border border-[#10B981]/25 bg-[#10B981]/5 rounded-full px-3 py-1">
                <InfinityIcon className="w-3.5 h-3.5" /> PRO · UNLIMITED SIMULATIONS
              </span>
            ) : pastDue ? (
              <span className="sim-mono inline-flex items-center gap-2 text-[11px] text-[#EF4444] border border-[#EF4444]/30 bg-[#EF4444]/5 rounded-full px-3 py-1">
                <AlertTriangle className="w-3.5 h-3.5" /> PAYMENT PAST DUE
              </span>
            ) : (
              <span className="sim-mono inline-flex items-center gap-2 text-[11px] text-[#00F0FF] border border-[#00F0FF]/25 bg-[#00F0FF]/5 rounded-full px-3 py-1">
                <Sparkles className="w-3.5 h-3.5" /> {remaining} OF {billing.trial_limit} FREE SIMS LEFT · THEN ${billing.plan_price ?? billing.price}/{billing.plan_interval === "year" ? "YR" : "MO"}
              </span>
            )}
            {canManageBilling && (
              <button
                data-testid="sim-manage-billing-btn"
                onClick={openPortal}
                disabled={portalLoading}
                className="sim-mono text-[11px] text-[#C2C5CE] hover:text-[#00F0FF] underline underline-offset-2 decoration-[#3f3f46] hover:decoration-[#00F0FF] transition-colors flex items-center gap-1 disabled:opacity-60"
              >
                {portalLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <CreditCard className="w-3 h-3" />}
                Manage billing
              </button>
            )}
          </div>
        )}

        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-9">
          <div className="sim-mono inline-flex items-center gap-2 text-[11px] text-[#00F0FF] border border-[#00F0FF]/25 bg-[#00F0FF]/5 rounded-full px-3 py-1 mb-5">
            <span className="w-1.5 h-1.5 rounded-full bg-[#00F0FF] animate-pulse" /> SYNTHETIC USER TESTING
          </div>
          <h1 className="sim-head text-4xl sm:text-5xl lg:text-6xl font-extrabold text-[#F5F5F5] leading-[1.02]">
            Test your product on<br /><span className="text-[#00F0FF]">10 synthetic users</span> in 75 seconds
          </h1>
          <p className="text-[#D4D4D8] text-base mt-4 max-w-xl mx-auto">
            Drop any website, App Store listing or mobile app binary. An orchestrated swarm of AI agents — each with distinct biases, attention spans and risk tolerance — scrutinizes it and returns an empirical usability scorecard.
          </p>
        </motion.div>

        {/* Input console */}
        <motion.div
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }}
          className="sim-card-elevated rounded-xl p-5 max-w-3xl mx-auto"
          data-testid="sim-console"
        >
          {/* Mode tabs */}
          <div className="grid grid-cols-3 gap-1.5 p-1 bg-[#0A0A0A] border border-[#27272A] rounded-lg mb-4" data-testid="sim-mode-tabs">
            {MODES.map(({ k, label, icon: Icon }) => (
              <button
                key={k}
                data-testid={`sim-mode-${k}`}
                onClick={() => setMode(k)}
                className={cn(
                  "flex items-center justify-center gap-1.5 h-9 rounded-md text-xs font-semibold transition-colors",
                  mode === k ? "bg-[#1A1A1A] text-[#00F0FF] border border-[#00F0FF]/30" : "text-[#C2C5CE] hover:text-[#F5F5F5]"
                )}
              >
                <Icon className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">{label}</span>
              </button>
            ))}
          </div>

          {/* ---- WEB URL ---- */}
          {mode === "web" && (
            <>
              <label className="sim-mono text-[11px] text-[#D4D4D8] uppercase tracking-wider">Target URL</label>
              <div className="flex flex-col sm:flex-row gap-2 mt-2">
                <input
                  data-testid="sim-url-input"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && run()}
                  placeholder="https://app.example.com/pricing"
                  className="flex-1 bg-[#0A0A0A] border border-[#27272A] rounded-lg px-4 h-12 text-[#F5F5F5] sim-mono text-sm placeholder:text-[#9AA0AC] focus:border-[#00F0FF]/50 focus:outline-none transition-colors"
                />
                <RunButton loading={loading} disabled={runDisabled} onClick={run} />
              </div>

              {/* Local / localhost helper */}
              <button
                data-testid="sim-local-help-toggle"
                onClick={() => setLocalHelp((o) => !o)}
                className="mt-2 flex items-center gap-1.5 sim-mono text-[11px] text-[#9AA0AC] hover:text-[#00F0FF] transition-colors"
              >
                <Terminal className="w-3 h-3" /> Testing a local / localhost site?
              </button>
              {localHelp && (
                <div className="mt-2 sim-card rounded-lg p-3.5 text-[13px] text-[#D4D4D8] leading-relaxed" data-testid="sim-local-help">
                  Expose your local site with a free tunnel, then paste the public URL above:
                  <div className="sim-mono text-[12px] text-[#9AA0AC] mt-2 space-y-1">
                    <p><span className="text-[#00F0FF]">ngrok:</span> <span className="text-[#F5F5F5]">ngrok http 3000</span> → paste the https://…ngrok-free.app URL</p>
                    <p><span className="text-[#00F0FF]">cloudflared:</span> <span className="text-[#F5F5F5]">cloudflared tunnel --url http://localhost:3000</span> → paste the https://…trycloudflare.com URL</p>
                  </div>
                  <p className="text-[#9AA0AC] text-[12px] mt-2">Need to sign in to the local app? Use the <span className="text-[#F5F5F5]">Sign-in / private access</span> options below.</p>
                </div>
              )}
            </>
          )}

          {/* ---- APP STORE URL ---- */}
          {mode === "store" && (
            <>
              <label className="sim-mono text-[11px] text-[#D4D4D8] uppercase tracking-wider">App Store / Google Play listing URL</label>
              <div className="flex flex-col sm:flex-row gap-2 mt-2">
                <input
                  data-testid="sim-store-url-input"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && run()}
                  placeholder="https://apps.apple.com/app/id… or https://play.google.com/store/apps/details?id=…"
                  className="flex-1 bg-[#0A0A0A] border border-[#27272A] rounded-lg px-4 h-12 text-[#F5F5F5] sim-mono text-sm placeholder:text-[#9AA0AC] focus:border-[#00F0FF]/50 focus:outline-none transition-colors"
                />
                <RunButton loading={loading} disabled={runDisabled} onClick={run} />
              </div>
              <p className="mt-2 flex items-start gap-1.5 text-[12px] text-[#9AA0AC]" data-testid="sim-store-hint">
                <Apple className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                The swarm evaluates your public store listing — icon, screenshots, title, ratings & description — exactly as a shopper sees it before installing.
              </p>
            </>
          )}

          {/* ---- APP FILE UPLOAD ---- */}
          {mode === "app" && (
            <div data-testid="sim-app-upload">
              <label className="sim-mono text-[11px] text-[#D4D4D8] uppercase tracking-wider">App package (.apk / .ipa)</label>
              <div className="mt-2">
                {!appFile ? (
                  <label
                    data-testid="sim-app-file-label"
                    className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-[#27272A] hover:border-[#00F0FF]/40 rounded-lg px-4 py-7 cursor-pointer transition-colors bg-[#0A0A0A]"
                  >
                    <input
                      data-testid="sim-app-file-input"
                      type="file"
                      accept=".apk,.ipa,application/vnd.android.package-archive"
                      className="hidden"
                      onChange={(e) => onPickApp(e.target.files?.[0])}
                    />
                    <FileUp className="w-6 h-6 text-[#00F0FF]" />
                    <span className="text-[#F5F5F5] text-sm font-semibold">Click to choose a .apk or .ipa file</span>
                    <span className="sim-mono text-[11px] text-[#9AA0AC]">Android or iOS package · up to {MAX_APP_MB} MB</span>
                  </label>
                ) : (
                  <div data-testid="sim-app-file-chip" className="flex items-center gap-3 bg-[#0A0A0A] border border-[#27272A] rounded-lg px-4 h-14">
                    {appFile.name.toLowerCase().endsWith(".ipa") ? <Apple className="w-5 h-5 text-[#007AFF] shrink-0" /> : <Smartphone className="w-5 h-5 text-[#3DDC84] shrink-0" />}
                    <div className="min-w-0 flex-1">
                      <p data-testid="sim-app-file-name" className="text-[#F5F5F5] text-sm sim-mono truncate">{appFile.name}</p>
                      <p className="text-[#9AA0AC] text-[11px] sim-mono">{(appFile.size / (1024 * 1024)).toFixed(1)} MB · {appFile.name.toLowerCase().endsWith(".ipa") ? "iOS" : "Android"}</p>
                    </div>
                    <button data-testid="sim-app-file-clear" onClick={() => setAppFile(null)} className="w-7 h-7 rounded flex items-center justify-center text-[#9AA0AC] hover:text-[#EF4444] hover:bg-[#EF4444]/10 transition-colors">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>

              {/* Optional screenshot */}
              <label className="sim-mono text-[11px] text-[#D4D4D8] uppercase tracking-wider block mt-4">Screenshot to judge <span className="text-[#9AA0AC] normal-case tracking-normal">(optional)</span></label>
              <div className="mt-2">
                {!appShot ? (
                  <label
                    data-testid="sim-app-screenshot-label"
                    className="flex items-center gap-2.5 border border-[#27272A] hover:border-[#00F0FF]/40 rounded-lg px-4 h-11 cursor-pointer transition-colors bg-[#0A0A0A]"
                  >
                    <input
                      data-testid="sim-app-screenshot-input"
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      className="hidden"
                      onChange={(e) => onPickShot(e.target.files?.[0])}
                    />
                    <ImageIcon className="w-4 h-4 text-[#9AA0AC]" />
                    <span className="text-[#C2C5CE] text-sm">Add a single app screenshot to evaluate instead of the store card</span>
                  </label>
                ) : (
                  <div data-testid="sim-app-screenshot-chip" className="flex items-center gap-3 bg-[#0A0A0A] border border-[#27272A] rounded-lg px-4 h-11">
                    <ImageIcon className="w-4 h-4 text-[#00F0FF] shrink-0" />
                    <p data-testid="sim-app-screenshot-name" className="text-[#F5F5F5] text-sm sim-mono truncate flex-1">{appShot.name}</p>
                    <button data-testid="sim-app-screenshot-clear" onClick={() => setAppShot(null)} className="w-7 h-7 rounded flex items-center justify-center text-[#9AA0AC] hover:text-[#EF4444] hover:bg-[#EF4444]/10 transition-colors">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>

              <div className="mt-3 flex items-start gap-1.5 text-[12px] text-[#9AA0AC]" data-testid="sim-app-note">
                <Bot className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>We parse the package (icon, metadata & permissions) and build a store-listing card for the swarm to judge. Add a screenshot above to have the agents evaluate that screen instead. No live browser is launched for app packages.</span>
              </div>

              <div className="mt-4">
                <RunButton loading={loading} disabled={runDisabled} onClick={run} full label="Analyze App" />
              </div>
            </div>
          )}

          {/* Environment mode */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-5">
            {Object.entries(ENV_MODES).map(([k, v]) => {
              const Icon = k === "b2b_saas" ? Building2 : ShoppingBag;
              const active = env === k;
              return (
                <button
                  key={k}
                  data-testid={`sim-env-${k}`}
                  onClick={() => setEnv(k)}
                  className={cn(
                    "text-left rounded-lg border p-3.5 flex items-center gap-3 transition-colors",
                    active ? "border-[#00F0FF]/60 bg-[#00F0FF]/5" : "border-[#27272A] bg-[#0A0A0A] hover:border-[#3f3f46]"
                  )}
                >
                  <div className={cn("w-9 h-9 rounded-md flex items-center justify-center shrink-0", active ? "bg-[#00F0FF]/15" : "bg-white/5")}>
                    <Icon className={cn("w-4 h-4", active ? "text-[#00F0FF]" : "text-[#D4D4D8]")} />
                  </div>
                  <div>
                    <p className="text-[#F5F5F5] text-sm font-semibold">{v.label}</p>
                    <p className="text-[#C2C5CE] text-xs">{v.sub}</p>
                  </div>
                  <div className={cn("ml-auto w-4 h-4 rounded-full border-2 transition-colors", active ? "border-[#00F0FF] bg-[#00F0FF]" : "border-[#3f3f46]")} />
                </button>
              );
            })}
          </div>

          {/* Viewport + auth (web & store only — app runs are judged on a phone) */}
          {mode === "app" ? (
            <div className="mt-4 flex items-center gap-1.5 sim-mono text-[11px] text-[#9AA0AC]">
              <Smartphone className="w-3.5 h-3.5" /> App packages are evaluated on a mobile device context.
            </div>
          ) : (
            <div className="flex flex-wrap items-center justify-between gap-3 mt-4">
              <div className="flex items-center gap-1.5 bg-[#0A0A0A] border border-[#27272A] rounded-lg p-1">
                {Object.entries(VIEWPORTS).map(([k, v]) => {
                  const Icon = VP_ICONS[k];
                  const active = viewport === k;
                  return (
                    <button
                      key={k}
                      data-testid={`sim-viewport-${k}`}
                      onClick={() => { setViewport(k); setVpTouched(true); }}
                      title={v.dims}
                      className={cn("flex items-center gap-1.5 px-2.5 py-1.5 rounded text-xs transition-colors",
                        active ? "bg-[#1A1A1A] text-[#F5F5F5]" : "text-[#C2C5CE] hover:text-[#D4D4D8]")}
                    >
                      <Icon className="w-3.5 h-3.5" /> {v.label}
                    </button>
                  );
                })}
              </div>
              {mode === "web" && (
                <button
                  data-testid="sim-auth-toggle"
                  onClick={() => setAuthOpen((o) => !o)}
                  className="flex items-center gap-1.5 sim-mono text-[11px] text-[#C2C5CE] hover:text-[#F5F5F5] transition-colors"
                >
                  <Lock className="w-3 h-3" /> Sign-in / private access
                  <ChevronDown className={cn("w-3 h-3 transition-transform", authOpen && "rotate-180")} />
                </button>
              )}
            </div>
          )}

          {mode === "web" && authOpen && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="mt-3 overflow-hidden">
              <div className="sim-card rounded-lg p-3.5 space-y-3">
                <p className="text-[#D4D4D8] text-[13px]">
                  Let the simulator evaluate the real logged-in experience — it signs in first, then runs the agents.
                </p>
                <div className="flex flex-wrap gap-2">
                  {[
                    { t: "form", label: "Form login", icon: KeyRound },
                    { t: "cookie", label: "Session cookies", icon: Cookie },
                    { t: "basic", label: "HTTP Basic", icon: Lock },
                  ].map(({ t, label, icon: Icon }) => (
                    <button key={t} data-testid={`sim-auth-type-${t}`} onClick={() => setAuth({ ...auth, type: t })}
                      className={cn("sim-mono text-[11px] px-3 py-1.5 rounded border transition-colors flex items-center gap-1.5",
                        auth.type === t ? "border-[#00F0FF]/50 text-[#00F0FF] bg-[#00F0FF]/5" : "border-[#27272A] text-[#C2C5CE] hover:border-[#3f3f46]")}>
                      <Icon className="w-3 h-3" /> {label}
                    </button>
                  ))}
                </div>

                {auth.type === "form" && (
                  <div className="space-y-2">
                    <input data-testid="sim-auth-login-url" value={auth.login_url} onChange={(e) => setAuth({ ...auth, login_url: e.target.value })}
                      placeholder="Login page URL (optional — defaults to the target URL)"
                      className="w-full bg-[#0A0A0A] border border-[#27272A] rounded px-3 h-9 text-sm text-[#F5F5F5] sim-mono placeholder:text-[#8E929C] focus:border-[#00F0FF]/40 focus:outline-none" />
                    <div className="grid grid-cols-2 gap-2">
                      <input data-testid="sim-auth-user" value={auth.username} onChange={(e) => setAuth({ ...auth, username: e.target.value })} placeholder="Email / username" className="bg-[#0A0A0A] border border-[#27272A] rounded px-3 h-9 text-sm text-[#F5F5F5] sim-mono placeholder:text-[#8E929C] focus:border-[#00F0FF]/40 focus:outline-none" />
                      <input data-testid="sim-auth-pass" type="password" value={auth.password} onChange={(e) => setAuth({ ...auth, password: e.target.value })} placeholder="Password" className="bg-[#0A0A0A] border border-[#27272A] rounded px-3 h-9 text-sm text-[#F5F5F5] sim-mono placeholder:text-[#8E929C] focus:border-[#00F0FF]/40 focus:outline-none" />
                    </div>
                    <p className="text-[#9AA0AC] text-[12px]">We auto-detect the username &amp; password fields and submit the form, including two-step (email → password) logins.</p>
                  </div>
                )}

                {auth.type === "cookie" && (
                  <div className="space-y-2">
                    <textarea data-testid="sim-auth-cookie" value={auth.token} onChange={(e) => setAuth({ ...auth, token: e.target.value })} rows={2}
                      placeholder="session=abc123; token=xyz789"
                      className="w-full bg-[#0A0A0A] border border-[#27272A] rounded px-3 py-2 text-sm text-[#F5F5F5] sim-mono placeholder:text-[#8E929C] focus:border-[#00F0FF]/40 focus:outline-none resize-none" />
                    <p className="text-[#9AA0AC] text-[12px]">Paste your site's session cookies (copy from your browser DevTools → Application → Cookies). Loaded before the page renders so it opens already signed-in.</p>
                  </div>
                )}

                {auth.type === "basic" && (
                  <div className="grid grid-cols-2 gap-2">
                    <input data-testid="sim-auth-user" value={auth.username} onChange={(e) => setAuth({ ...auth, username: e.target.value })} placeholder="Username" className="bg-[#0A0A0A] border border-[#27272A] rounded px-3 h-9 text-sm text-[#F5F5F5] sim-mono placeholder:text-[#8E929C] focus:border-[#00F0FF]/40 focus:outline-none" />
                    <input data-testid="sim-auth-pass" type="password" value={auth.password} onChange={(e) => setAuth({ ...auth, password: e.target.value })} placeholder="Password" className="bg-[#0A0A0A] border border-[#27272A] rounded px-3 h-9 text-sm text-[#F5F5F5] sim-mono placeholder:text-[#8E929C] focus:border-[#00F0FF]/40 focus:outline-none" />
                  </div>
                )}

                <p className="text-[#8E929C] text-[12px]">Encrypted in transit and purged after the run. Works on public, staging and tunnelled local URLs.</p>
              </div>
            </motion.div>
          )}
        </motion.div>
      </div>

      {/* Recent runs */}
      {recent.length > 0 && (
        <div className="mt-8 max-w-3xl mx-auto">
          <p className="sim-mono text-[11px] text-[#C2C5CE] uppercase tracking-wider mb-3 flex items-center gap-2">
            <Clock className="w-3 h-3" /> Recent simulations
          </p>
          <div className="space-y-2">
            {recent.map((r) => (
              <button
                key={r.sim_id}
                data-testid={`sim-recent-${r.sim_id}`}
                onClick={() => navigate(`/sim/s/${r.sim_id}`)}
                className="w-full sim-card rounded-lg px-4 py-3 flex items-center gap-3 hover:border-[#3f3f46] transition-colors group"
              >
                <span className={cn("sim-mono text-[10px] px-2 py-0.5 rounded border",
                  r.environment_mode === "b2b_saas" ? "text-[#00F0FF] border-[#00F0FF]/30" : "text-[#007AFF] border-[#007AFF]/30")}>
                  {r.environment_mode === "b2b_saas" ? "B2B" : "B2C"}
                </span>
                <span className="text-sm text-[#F5F5F5] truncate flex-1 text-left sim-mono">{r.target_url}</span>
                {r.synthesis?.health_score != null && (
                  <span className="sim-mono text-sm font-bold text-[#F5F5F5]">{r.synthesis.health_score}</span>
                )}
                <span className={cn("sim-mono text-[10px]", r.status === "COMPLETED" ? "text-[#10B981]" : r.status === "FAILED" ? "text-[#EF4444]" : "text-[#F59E0B]")}>
                  {r.status}
                </span>
                <ArrowRight className="w-3.5 h-3.5 text-[#9AA0AC] group-hover:text-[#00F0FF] transition-colors" />
              </button>
            ))}
          </div>
        </div>
      )}
    </SimLayout>
  );
}

function RunButton({ loading, disabled, onClick, full, label = "Run Sim" }) {
  return (
    <button
      data-testid="sim-run-btn"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "sim-tracing-btn h-12 px-6 rounded-lg bg-[#00F0FF] text-[#0A0A0A] font-bold text-sm flex items-center justify-center gap-2 hover:bg-[#33f3ff] transition-colors disabled:opacity-60 disabled:cursor-not-allowed",
        full && "w-full"
      )}
    >
      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4 fill-current" />}
      {loading ? "Launching…" : label}
    </button>
  );
}
